import React, { useState } from "react";
import Logo from "../assets/favicon.jpeg";
import { Eye, EyeOff, Trash2, AlertTriangle, X } from "lucide-react";
import { getApiBase } from "../utils/apiBase";

type DeleteAccountProps = {
  onCancel?: () => void;
  onDeleted?: () => void;
};

const DeleteAccount: React.FC<DeleteAccountProps> = ({ onCancel, onDeleted }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const API_BASE = getApiBase();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!email.trim() || !password) {
      setError("Email and password are required.");
      return;
    }
    setShowConfirm(true);
  };

 const handleConfirmDelete = async () => {
  setDeleting(true);
  setError(null);
  try {
    const res = await fetch(`${API_BASE}/api/users/self-delete`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email: email.trim(), password }),
    });
    const data = await res.json();
    if (!res.ok) {
      setShowConfirm(false);
      setError(data.message || "Failed to delete account. Please try again.");
      return;
    }
    localStorage.removeItem("authToken");
    localStorage.removeItem("authUser");
    setShowConfirm(false);
    onDeleted?.();
  } catch {
    setShowConfirm(false);
    setError("Network error. Please try again.");
  } finally {
    setDeleting(false);
  }
};

  const inputStyle: React.CSSProperties = {
    width: "100%",
    border: "1px solid #e5e7eb",
    borderRadius: "10px",
    padding: "10px 14px",
    fontSize: "14px",
    color: "#111827",
    outline: "none",
    boxSizing: "border-box",
    background: "#fafafa",
  };

  const labelStyle: React.CSSProperties = {
    display: "block",
    fontSize: "12px",
    fontWeight: 500,
    color: "#374151",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    marginBottom: "6px",
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "#f8f7f5",
        padding: 16,
        fontFamily: "'DM Sans', sans-serif",
      }}
    >
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600&display=swap');`}</style>

      {/* Confirmation Modal */}
      {showConfirm && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 50,
            padding: 16,
          }}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: "20px",
              padding: "32px",
              width: 400,
              maxWidth: "100%",
              boxShadow: "0 20px 60px rgba(0,0,0,0.18)",
              border: "1px solid #f3f4f6",
            }}
          >
            {/* Warning icon */}
            <div
              style={{
                width: 52,
                height: 52,
                borderRadius: "14px",
                background: "#fff5f5",
                border: "1px solid #fecaca",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginBottom: "20px",
              }}
            >
              <AlertTriangle size={24} color="#b91c1c" />
            </div>

            <h3
              style={{
                margin: "0 0 8px",
                fontSize: "18px",
                fontWeight: 600,
                color: "#111827",
              }}
            >
              Are you absolutely sure?
            </h3>
            <p
              style={{
                margin: "0 0 24px",
                fontSize: "14px",
                color: "#6b7280",
                lineHeight: "1.55",
              }}
            >
              This action <strong style={{ color: "#111827" }}>cannot be undone</strong>. Your
              account and all associated data will be permanently deleted.
            </p>

            <div style={{ display: "flex", gap: "10px" }}>
              <button
                onClick={() => setShowConfirm(false)}
                disabled={deleting}
                style={{
                  flex: 1,
                  height: "44px",
                  borderRadius: "10px",
                  border: "1px solid #e5e7eb",
                  background: "#fff",
                  color: "#374151",
                  fontSize: "14px",
                  fontWeight: 600,
                  cursor: deleting ? "not-allowed" : "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "6px",
                }}
              >
                <X size={15} />
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                disabled={deleting}
                style={{
                  flex: 1,
                  height: "44px",
                  borderRadius: "10px",
                  border: "none",
                  background: deleting ? "#fca5a5" : "#dc2626",
                  color: "#fff",
                  fontSize: "14px",
                  fontWeight: 600,
                  cursor: deleting ? "not-allowed" : "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "6px",
                  transition: "background 0.15s",
                }}
              >
                <Trash2 size={15} />
                {deleting ? "Deleting…" : "Yes, delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={{ width: 400, maxWidth: "100%" }}>
        {/* Logo + brand */}
        <div style={{ textAlign: "center", marginBottom: "32px" }}>
          <img
            src={Logo}
            alt="JAC Logo"
            style={{
              width: 64,
              height: 64,
              borderRadius: "16px",
              objectFit: "cover",
              marginBottom: "14px",
            }}
          />
          <h1 style={{ margin: 0, fontSize: "22px", fontWeight: 600, color: "#111827" }}>
            Scan Pay Go
          </h1>
          <p style={{ margin: "4px 0 0", fontSize: "14px", color: "#9ca3af" }}>Admin dashboard</p>
        </div>

        {/* Card */}
        <div
          style={{
            background: "#fff",
            borderRadius: "20px",
            padding: "32px",
            border: "1px solid #f3f4f6",
            boxShadow: "0 4px 24px rgba(0,0,0,0.06)",
          }}
        >
          {/* Header with danger badge */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: "24px",
            }}
          >
            <h2 style={{ margin: 0, fontSize: "18px", fontWeight: 600, color: "#111827" }}>
              Delete account
            </h2>
            <span
              style={{
                fontSize: "11px",
                fontWeight: 600,
                color: "#b91c1c",
                background: "#fff5f5",
                border: "1px solid #fecaca",
                borderRadius: "6px",
                padding: "3px 8px",
                textTransform: "uppercase",
                letterSpacing: "0.05em",
              }}
            >
              Danger zone
            </span>
          </div>

          {/* Warning banner */}
          <div
            style={{
              marginBottom: "20px",
              padding: "12px 14px",
              borderRadius: "10px",
              background: "#fffbeb",
              border: "1px solid #fde68a",
              fontSize: "13px",
              color: "#92400e",
              lineHeight: "1.5",
            }}
          >
            Enter your credentials to confirm. This will permanently delete your account.
          </div>

          {error && (
            <div
              style={{
                marginBottom: "16px",
                padding: "12px 14px",
                borderRadius: "10px",
                background: "#fff5f5",
                border: "1px solid #fecaca",
                fontSize: "13px",
                color: "#b91c1c",
              }}
            >
              {error}
            </div>
          )}

          <form
            onSubmit={handleSubmit}
            style={{ display: "flex", flexDirection: "column", gap: "16px" }}
          >
            <div>
              <label style={labelStyle}>Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                autoComplete="username"
                required
                style={inputStyle}
                onFocus={(e) => (e.target.style.borderColor = "#F1561D")}
                onBlur={(e) => (e.target.style.borderColor = "#e5e7eb")}
              />
            </div>

            <div>
              <label style={labelStyle}>Password</label>
              <div style={{ position: "relative" }}>
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  autoComplete="current-password"
                  required
                  style={{ ...inputStyle, padding: "10px 44px 10px 14px" }}
                  onFocus={(e) => (e.target.style.borderColor = "#F1561D")}
                  onBlur={(e) => (e.target.style.borderColor = "#e5e7eb")}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((s) => !s)}
                  style={{
                    position: "absolute",
                    right: "12px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    color: "#9ca3af",
                    display: "flex",
                    alignItems: "center",
                    padding: 0,
                  }}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              style={{
                marginTop: "4px",
                height: "44px",
                borderRadius: "10px",
                border: "none",
                background: loading ? "#fca5a5" : "#dc2626",
                color: "#fff",
                fontSize: "15px",
                fontWeight: 600,
                cursor: loading ? "not-allowed" : "pointer",
                transition: "background 0.15s",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
              }}
            >
              <Trash2 size={16} />
              {loading ? "Processing…" : "Delete my account"}
            </button>
          </form>

          {onCancel && (
            <div style={{ marginTop: "20px", textAlign: "center" }}>
              <button
                type="button"
                onClick={onCancel}
                style={{
                  fontSize: "13px",
                  color: "#F1561D",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  fontWeight: 500,
                }}
              >
                Go back
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DeleteAccount;
